import React, { useState, useEffect } from "react";
import { useActiveDeal } from "../../../lib/deals/useActiveDeal";
import DealGuard from "../../../components/common/DealGuard";
import { buildCashFlowStatement } from "../../../lib/finance/cashflow";
import { Scale } from "../../../lib/pl/format";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../components/ui/select";
import CashflowHeaderKPIs from "../../../components/cf/CashflowHeaderKPIs";
import CashflowTable from "../../../components/cf/CashflowTable";
import { ICurrency } from "@/types";
import DebtScheduleTable from "@/components/cf/DebtScheduleTable";
import { getPayrollDataById, PayrollDataRecord } from "@/hooks/payrollDataService";

export default function CashFlowStatement() {
  const { dealId, deal, status } = useActiveDeal();
  const [scale, setScale] = useState<Scale>("full");
  const [payrollData, setPayrollData] = useState<PayrollDataRecord | null>(
    null,
  );

  // Загружаем payroll по dataId из kpis.payroll.dataId
  useEffect(() => {
    const dataId = deal?.kpis?.payroll?.dataId;
    if (!dataId) return;

    getPayrollDataById(dataId).then((result) => {
      setPayrollData(result);
    });
  }, [deal?.kpis?.payroll?.dataId]);

  if (status !== "ready") return <DealGuard status={status} />;

  // Передаём payrollData вторым аргументом — positions считаются по departmentId
  const cashFlowResult = buildCashFlowStatement(deal, payrollData);

  return (
    <div className="w-full max-w-[95vw] mx-auto space-y-6">
      <div className="w-full rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Cash Flow Statement
            </h1>
            <p className="text-sm text-slate-600">
              Unlevered and levered cash flow projections with debt service
              analysis
            </p>
            <div className="mt-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 inline-block">
              Calculated • Read-only
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Display</span>
              <Select value={scale} onValueChange={(v) => setScale(v as Scale)}>
                <SelectTrigger className="h-9 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="thousands">Thousands</SelectItem>
                  <SelectItem value="millions">Millions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <CashflowHeaderKPIs
        exitYear={cashFlowResult.exitYear}
        currency={deal.currency as ICurrency}
        kpis={cashFlowResult.kpis}
      />

      <CashflowTable
        rows={cashFlowResult.rows}
        currency={deal.currency}
        scale={scale}
        exitYear={cashFlowResult.exitYear}
      />

      <DebtScheduleTable
        rows={cashFlowResult.debtSchedule.rows}
        currency={deal.currency}
        scale={scale}
        exitYear={cashFlowResult.exitYear}
        totalLoanAmount={cashFlowResult.debtSchedule.totalLoanAmount}
        totalInterest={cashFlowResult.debtSchedule.totalInterest}
        totalPrincipalRepaid={cashFlowResult.debtSchedule.totalPrincipalRepaid}
      />

      <div className="w-full rounded-lg bg-slate-50 border border-slate-200 p-4">
        <div className="text-sm text-slate-600">
          <strong>Note:</strong> Cash flows are calculated from your P&L
          projections, financing structure, and exit assumptions.
          {cashFlowResult.exitYear && (
            <span>
              {" "}
              Property disposal occurs at end of Year {cashFlowResult.exitYear}.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
